<template>
  <div class="flex items-center gap-4">
    <div
      class="flex items-center justify-center text-primary-700 rounded-full fill-current h-11 w-11 overflow-hidden"
      :class="{
        'bg-neutral-200': highContrast,
        'bg-neutral-100': !lowContrast,
        'bg-neutral-white border border-neutral-300 border-solid': lowContrast,
      }"
    >
      <img v-if="iconUrl" :src="iconUrl" class="h-full w-full object-contain" />
      <component
        :is="icon"
        v-else
        ref="iconRef"
        :class="{
          'h-5 w-5': !largeIcon,
          'h-7 w-7': largeIcon,
          'fill-path-current': !isSvgWithUnsetFill,
        }"
      />
    </div>

    <div
      :class="{
        'md:text-3xl text-2xl font-faktum font-semibold': title,
        'text-lg font-semibold': !title,
      }"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Component } from 'vue';

import * as Icons from '@pulse/components/icons';

import { seasonalNavIcon } from '~/composables/useSeasonalIcons';

const props = defineProps({
  // An explicit icon component; takes precedence over `name` (used for seasonal icons that have no Pulse name).
  component: {
    default: null,
    type: [Object, Function] as PropType<Component | null>,
  },
  highContrast: {
    default: false,
    type: Boolean,
  },
  iconUrl: {
    default: '',
    type: String,
  },
  largeIcon: {
    default: false,
    type: Boolean,
  },
  lowContrast: {
    default: false,
    type: Boolean,
  },
  name: {
    default: '',
    type: String as PropType<keyof typeof iconMap>,
  },
  skipIconNameTransform: {
    default: false,
    type: Boolean,
  },
  title: {
    default: false,
    type: Boolean,
  },
});
const iconRef = ref<HTMLElement | null>(null);

const isSvgWithUnsetFill = ref(false);
const checkSvgFill = () => {
  const el = iconRef.value?.$el ?? iconRef.value;
  if (el instanceof SVGElement && el.tagName.toLowerCase() === 'svg') {
    isSvgWithUnsetFill.value = el.getAttribute('fill') === 'unset';
  }
};

onMounted(checkSvgFill);
const iconMap = {
  ...Object.entries(Icons).reduce(
    (acc, [key, value]) => {
      const name = props.skipIconNameTransform
        ? key
        : key
            .replace(/^Line/, '')
            .split(/(?=[A-Z])/)
            .map(s => s.toLowerCase())
            .join('-');

      acc[name] = value;

      return acc;
    },
    {} as Record<string, any>,
  ),
};

const slots = useSlots();

// The header text doubles as the sidebar label, which is how the seasonal icon for this page is looked up.
const label = computed(() =>
  (slots.default?.() ?? [])
    .map(node => (typeof node.children === 'string' ? node.children : ''))
    .join('')
    .trim(),
);

const icon = computed(() => {
  return props.component ?? seasonalNavIcon(label.value, iconMap[props.name]);
});
</script>
